/**
 * Categorical palettes — spec §G8: "Categorical order fixed (country palette) — never
 * repaint survivors on filter".
 *
 * The country palette is the mockup's `CPAL`, and the rule attached to it is the reason it
 * lives in one module rather than at each call site: Burkina Faso is green in the dock
 * popover's "By country" bars, in the §F4 splash bars and in the §F7 dashboard's
 * cross-country lines, whichever countries happen to be in scope. A palette indexed by
 * position — `colors[i]` down a filtered array — would recolour Mali the moment Burkina
 * Faso dropped out of the filter, which is exactly what §G8 forbids.
 *
 * These four are also *not* the domain colours and *not* the §G status colours (§G8: "status
 * colours … never reused as series colours"), so a green bar here never reads as "open".
 */
import type { Country } from './state';

/** ISO3 → the country's fixed series colour (mockup `CPAL`). */
export const COUNTRY_COLORS: Record<Exclude<Country, 'ALL'>, string> = {
  BFA: '#2E7D32',
  MLI: '#2B6CB0',
  NER: '#C77B21',
  NGA: '#8E3B62',
};

/**
 * §G8's "fixed order" — the sequence every country chart lays its categories out in,
 * independent of whatever order an API happened to answer in.
 */
export const COUNTRY_ORDER: Exclude<Country, 'ALL'>[] = ['BFA', 'MLI', 'NER', 'NGA'];

/** Fallback ink for an unknown code, so a chart never renders a `undefined` fill. */
export const UNKNOWN_SERIES_COLOR = '#8B958A';

/** The colour for one ISO3 code; unknown codes get the neutral ink rather than nothing. */
export function countryColor(iso3: string): string {
  return COUNTRY_COLORS[iso3 as Exclude<Country, 'ALL'>] ?? UNKNOWN_SERIES_COLOR;
}

/**
 * Baseline charts (client, 7 Oct 2026): one colour per chart, sorted from highest to lowest,
 * deeper for higher and lighter for lower. This amends §G8's country palette for the
 * Baseline section only; every other country chart keeps `COUNTRY_COLORS`.
 */
export const BASELINE_HUE = '#2E7D32';
/** The pale end the ramp mixes toward. */
export const BASELINE_PALE = '#E8F2E8';
/**
 * How far toward the pale end the lowest bar goes. The spec allows up to 35 %; 30 % is the
 * furthest that keeps the lightest bar at 3:1 against the white panel (35 % measures 2.85:1).
 */
export const RANK_TINT_MAX = 0.3;

function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** `hex` mixed toward `toward` by `t` (0 = `hex`, 1 = `toward`), as `#rrggbb`. */
export function mixHex(hex: string, toward: string, t: number): string {
  const a = hexRgb(hex);
  const b = hexRgb(toward);
  const k = Math.min(1, Math.max(0, t));
  return `#${a
    .map((c, i) => Math.round(c + (b[i]! - c) * k).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/**
 * The fill of the bar ranked `i` (0 = highest) of `n`: the full hue at the top, then linear
 * in rank toward `BASELINE_PALE`, ending `RANK_TINT_MAX` of the way there. One bar is the
 * full hue.
 */
export function rankTint(hex: string, i: number, n: number): string {
  if (n <= 1) return mixHex(hex, hex, 0);
  const rank = Math.min(Math.max(i, 0), n - 1);
  return mixHex(hex, BASELINE_PALE, (rank / (n - 1)) * RANK_TINT_MAX);
}
